import React, { useEffect, useMemo, useState } from 'react'

import { productCodeLittleLoans } from '@ilg-totallymoney/cardloanutils'
import { JourneyType } from '@ilg-totallymoney/types'
import Image from 'next/image'
import styled from 'styled-components'

import Anchor from '@ilg-totallymoney/ui/components/Anchor'
import Badge from '@ilg-totallymoney/ui/components/Badge'
import Box from '@ilg-totallymoney/ui/components/Box'
import Card from '@ilg-totallymoney/ui/components/Card'
import Divider from '@ilg-totallymoney/ui/components/Divider'
import Flex from '@ilg-totallymoney/ui/components/Flex'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import Row from '@ilg-totallymoney/ui/components/Row'
import Stack from '@ilg-totallymoney/ui/components/Stack'
import Text from '@ilg-totallymoney/ui/components/Text'
import Icon from '@ilg-totallymoney/ui/icons/Icon'
import theme from '@ilg-totallymoney/ui/theme'

import { CreditCardAlternativeProduct } from '@appTypes/EligibilityApi.interface'

import useAppQueryState from '@hooks/useAppQueryState'
import useGetLoanApplicationUrl from '@hooks/useGetLoanApplicationUrl'
import useGetLoansAlternativeProductApplicationUrl from '@hooks/useGetLoansAlternativeProductApplicationUrl'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'
import useLogEvents from '@hooks/useLogEvents'
import useLogEvents2 from '@hooks/useLogEvents2'

import littleLoansLogo from '@images/little-loans-logo.png'
import littleLoansRobot from '@images/little-loans-robot.svg'

import { useSingleProviderScoreMutation } from '@queries/useSingleProviderScoreMutation'

import { getAppConfig } from '@utils/getAppConfig'
import { getClickId } from '@utils/getClickId'

import LittleLoansApplyButtons from '../LittleLoansApplyButtons'
import LittleLoansModal from '../LittleLoansModal'
import LoanCategoryTooltip from '../LoanCategoryTooltip'
import LoanTypeModal from '../LoanTypeModal'

const TealText = styled.span`
    color: ${theme.teal140};
`

const ProductInfo = ({ title }: { title: string }) => (
    <Flex
        mb="space05"
        alignItems="flex-start"
        backgroundColor="grey20"
        p="space10"
        borderRadius="borderRadius10"
        maxHeight={['100%', '64px']}
    >
        <Flex alignItems="flex-start" gridColumnGap="space05">
            <Box mt="space05">
                <Icon color={theme.teal140} icon="offersCheckMark" size={16} />
            </Box>
            <Text variant="100">{title}</Text>
        </Flex>
    </Flex>
)

const DEFAULT_LOAN_AMOUNT = 2000
const DEFAULT_LOAN_TERM = 24

const LittleLoansOfferCard = ({
    inscalarJourney = 'personal_loans',
    productPosition = 0,
}: {
    inscalarJourney?: Extract<JourneyType, 'homeowner_loans' | 'personal_loans'>
    productPosition?: number
}) => {
    const logEvents = useLogEvents()
    const { logAlternativeProductClickEvent } = useLogEvents2()
    const { trackClickoutAlt } = useInscalarTrackEvent()
    const { appQueryState } = useAppQueryState()
    const appConfig = getAppConfig()
    const clickId = getClickId()

    const [isLittleLoansModalOpen, setLittleLoansModalOpen] = useState(false)
    const [isLoanTypeModalOpen, setLoanTypeModalOpen] = useState(false)

    const getLoanApplicationUrl = useGetLoanApplicationUrl()
    const getLoansAlternativeProductApplicationUrl =
        useGetLoansAlternativeProductApplicationUrl()

    const { mutate, data, isError, isLoading } =
        useSingleProviderScoreMutation()

    useEffect(() => {
        if (!appQueryState.loansScoreId) {
            return
        }
        mutate({
            loansScoreId: appQueryState.loansScoreId,
            loanAmount: DEFAULT_LOAN_AMOUNT,
            loanTerm: DEFAULT_LOAN_TERM,
            loanPurpose: 'Other',
            customerId: appQueryState.customerId,
            providerName: 'LittleLoans',
        })
    }, [
        appQueryState.customerId,
        appQueryState.scoreId,
        appQueryState.loansScoreId,
    ])

    const littleLoansApplicationUrl = useMemo(() => {
        if (isError || !data?.scores?.length) {
            return getLoansAlternativeProductApplicationUrl({
                productId: productCodeLittleLoans,
                loanAmount: DEFAULT_LOAN_AMOUNT,
                loanTerm: DEFAULT_LOAN_TERM,
                tablePosition: 0,
            })
        }

        return getLoanApplicationUrl({
            loan: data.scores[0],
            clickId,
            loanAmount: data.loanAmount,
            loanTerm: data.loanTerm,
            tablePosition: 0,
            thirdPartyReference: '',
        })
    }, [
        isError,
        data,
        getLoansAlternativeProductApplicationUrl,
        getLoanApplicationUrl,
    ])

    const handleClickout = () => {
        logEvents.logAlternativeProductApplyEvent({
            productCode: productCodeLittleLoans,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            rowNumber: 1,
            trigger: 'row',
        })

        trackClickoutAlt({
            step: 'credit_cards_alternative_products_table',
            product: {
                name: 'Little Loans',
                productCode: productCodeLittleLoans,
                apr: 79.5,
            } as CreditCardAlternativeProduct,
            journey: inscalarJourney,
            journeyCategory: 'loans',
            productPosition,
        })

        logAlternativeProductClickEvent({
            altCreditCard: {
                name: 'Little Loans',
                productCode: productCodeLittleLoans,
                apr: 79.5,
            },
            category: 'credit_cards_alt',
            clickId,
            productRankOrder: null,
            productShownOrder: 1,
        })
    }

    return (
        <>
            <Card py="space30" px="space20" hasShadow={true} hasBorder={true}>
                <Box mb="space20">
                    <Badge
                        text="All credit scores welcome"
                        impact="high"
                        size="medium"
                    />
                </Box>
                <Flex mb="space20" alignItems="center" gridColumnGap="space20">
                    <Box width={[104, 156, 172]} height={[52, 78, 86]}>
                        <Image
                            src={littleLoansLogo}
                            alt="Little Loans"
                            loading="lazy"
                            style={{ width: '100%', height: 'auto' }}
                        />
                    </Box>
                    <Flex
                        gridColumnGap="space10"
                        alignItems={['flex-start', 'center']}
                        flexDirection={['column', 'row']}
                    >
                        <Box mb={['space05', 0]}>
                            <Heading variant="40">Little Loans</Heading>
                        </Box>

                        <LoanCategoryTooltip
                            loanCategory="Personal Loan"
                            loanId={productCodeLittleLoans}
                            onShowLoanCategoryModal={() =>
                                setLoanTypeModalOpen(true)
                            }
                        />
                    </Flex>
                </Flex>
                <Flex
                    mb="space20"
                    alignItems="center"
                    gridColumnGap={['space20', 'space40']}
                    justifyContent={['space-between', 'left']}
                >
                    <Heading variant="100">
                        Borrow up to <TealText>£10,000</TealText>
                    </Heading>
                    <Image
                        src={littleLoansRobot}
                        alt="Little Loans robot logo"
                        loading="lazy"
                    />
                </Flex>
                <Flex
                    justifyContent="space-between"
                    mb="space20"
                    gridColumnGap={['0', '0', 'space20']}
                >
                    <Box
                        display="grid"
                        gridTemplateColumns={[
                            '1fr',
                            '1fr',
                            'repeat(2, 1fr)',
                            'repeat(4, 1fr)',
                        ]}
                        gridRowGap="space05"
                        gridColumnGap="space10"
                        width="100%"
                    >
                        <ProductInfo title="Funds same day if approved" />
                        <ProductInfo title="No fees, no hidden costs" />
                        <ProductInfo title="Quick decision online" />
                        <ProductInfo title="We'll prefill your details" />
                    </Box>
                    <Box display={['none', 'none', 'flex']}>
                        <LittleLoansApplyButtons
                            applicationUrl={littleLoansApplicationUrl}
                            isLoading={isLoading}
                            onApply={handleClickout}
                            onMoreInfoClick={() =>
                                setLittleLoansModalOpen(true)
                            }
                        />
                    </Box>
                </Flex>
                <Box display={['none', 'none', 'initial']}>
                    <Divider
                        color="neutral160"
                        spacing="space20"
                        borderStyle="dashed"
                    />
                </Box>
                <Box display={['inline', 'inline', 'none']}>
                    <Box mb="space20">
                        <LittleLoansApplyButtons
                            applicationUrl={littleLoansApplicationUrl}
                            isLoading={isLoading}
                            onApply={handleClickout}
                            onMoreInfoClick={() =>
                                setLittleLoansModalOpen(true)
                            }
                        />
                    </Box>
                </Box>
                <Stack
                    space="space10"
                    flexDirection="column"
                    alignItems="center"
                >
                    <Text variant="60" weight="bold">
                        79.5% APR representative
                    </Text>
                    <Text textAlign="center" variant="60" color="light">
                        Warning: Late repayment can cause you serious money
                        problems. For help, go to{' '}
                        <Anchor
                            style={{ display: 'inline' }}
                            target="_blank"
                            rel="noopener"
                            href="https://www.moneyhelper.org.uk"
                            aria-label="Opens in a new tab to MoneyHelper website"
                        >
                            moneyhelper.org.uk
                        </Anchor>
                        .
                    </Text>
                    <Text textAlign="center" variant="60" color="light">
                        Digitonomy Limited, trading as Little Loans, is a credit
                        broker, not a lender.
                    </Text>
                </Stack>
            </Card>

            <LoanTypeModal
                showModal={isLoanTypeModalOpen}
                onModalClose={() => setLoanTypeModalOpen(false)}
            />
            <LittleLoansModal
                showModal={isLittleLoansModalOpen}
                onModalClose={() => setLittleLoansModalOpen(false)}
            />
        </>
    )
}

export default LittleLoansOfferCard
